import React, { useState } from "react";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MoreHorizontal, Pencil, ArrowRightLeft, Tags, GitFork, CheckSquare, StickyNote, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

const STATUS = ["active", "inactive", "prospect", "archived"];
const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

function Fld({ label, children }) {
  return <div><Label className="text-xs font-semibold text-slate-700 mb-1 block">{label}</Label>{children}</div>;
}

export function BulkClassifyDialog({ personIds, classTypes, onClose, onDone }) {
  const [action, setAction] = useState("add");
  const [classification, setClassification] = useState("");
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const count = personIds.length;
  const clsLabel = classTypes.find((c) => c.key === classification)?.label;
  const submit = async () => {
    if (!classification) return;
    setSaving(true);
    try {
      const r = await api.post("/persons/bulk-classifications", { person_ids: personIds, classification, action });
      setResult(r.data);
      toast.success(`${action === "add" ? "Added" : "Removed"} for ${r.data.changed} ${r.data.changed === 1 ? "person" : "people"}`);
      onDone?.();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-md" data-testid="bulk-classify-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Tags className="w-5 h-5 text-[#006090]" /> Edit Classifications</DialogTitle>
          <DialogDescription>
            Add or remove a classification for {count} selected {count === 1 ? "Person" : "People"}. Changes keep
            persons.classifications and the People list counts in sync. To reclassify (e.g. Lead → Candidate),
            add the new classification and remove the old one.
          </DialogDescription>
        </DialogHeader>
        {!result ? (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Fld label="Action">
                <Select value={action} onValueChange={setAction}>
                  <SelectTrigger data-testid="bulk-classify-action"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white"><SelectItem value="add">Add</SelectItem><SelectItem value="remove">Remove</SelectItem></SelectContent>
                </Select>
              </Fld>
              <Fld label="Classification">
                <Select value={classification} onValueChange={setClassification}>
                  <SelectTrigger data-testid="bulk-classify-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="bg-white max-h-[280px]">
                    {classTypes.filter((c) => c.assignable !== false).map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Fld>
            </div>
            {classification && (
              <p className="text-[11px] text-slate-500">{action === "add" ? "Add" : "Remove"} <b>{clsLabel}</b> {action === "add" ? "to" : "from"} <b>{count}</b> {count === 1 ? "Person" : "People"}.</p>
            )}
          </div>
        ) : (
          <div className="space-y-2 text-sm" data-testid="bulk-classify-result">
            <p><b>{result.changed}</b> updated.</p>
            {result.blocked?.length > 0 && (
              <div className="rounded-md bg-rose-50 border border-rose-200 p-2.5">
                <p className="text-rose-700 font-medium text-xs mb-1">{result.blocked.length} could not be changed:</p>
                <ul className="text-[11px] text-rose-600 list-disc pl-4">{result.blocked.map((b, i) => <li key={i}>{b.name || b.id}: {b.reason}</li>)}</ul>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          {!result ? (
            <>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={submit} disabled={!classification || saving} data-testid="bulk-classify-confirm" className="bg-[#006090] hover:bg-[#004E75]">Apply to {count} {count === 1 ? "Person" : "People"}</Button>
            </>
          ) : (
            <Button onClick={onClose} data-testid="bulk-classify-close" className="bg-[#006090] hover:bg-[#004E75]">Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function PeopleRowMenu({ person, canManage, onQuickEdit, onMoveOrg, onEditClass, onNavigate }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid={`row-menu-${person.id}`} onClick={(e) => e.stopPropagation()}
          className="p-1.5 rounded hover:bg-slate-100 text-slate-500"><MoreHorizontal className="w-4 h-4" /></button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-white w-52" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuItem data-testid={`menu-quick-edit-${person.id}`} onClick={() => onQuickEdit(person)}><Pencil className="w-4 h-4 mr-2" /> Quick Edit</DropdownMenuItem>
        <DropdownMenuItem data-testid={`menu-edit-class-${person.id}`} onClick={() => onEditClass(person)}><Tags className="w-4 h-4 mr-2" /> Edit Classifications</DropdownMenuItem>
        {canManage && <DropdownMenuItem data-testid={`menu-move-org-${person.id}`} onClick={() => onMoveOrg(person)}><ArrowRightLeft className="w-4 h-4 mr-2" /> Move Organization</DropdownMenuItem>}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => onNavigate(`/people/detail/${person.id}`, "relationships")}><GitFork className="w-4 h-4 mr-2" /> Manage Relationships</DropdownMenuItem>
        <DropdownMenuItem onClick={() => onNavigate(`/people/detail/${person.id}`, "tasks")}><CheckSquare className="w-4 h-4 mr-2" /> Create Task</DropdownMenuItem>
        <DropdownMenuItem onClick={() => onNavigate(`/people/detail/${person.id}`, "notes")}><StickyNote className="w-4 h-4 mr-2" /> Add Note</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function QuickEditDialog({ person, orgs, classTypes, onClose, onSaved }) {
  const [form, setForm] = useState({
    email: person.email || "", mobile: person.mobile || person.phone || "", status: person.status || "active",
    organization_id: person.organization_id || "", agent_number: person.custom_fields?.agent_number || "",
  });
  const [classes, setClasses] = useState(person.classifications || []);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleClass = async (key) => {
    try {
      if (classes.includes(key)) { await api.delete(`/persons/${person.id}/classifications/${key}`); setClasses(classes.filter((c) => c !== key)); }
      else { await api.post(`/persons/${person.id}/classifications`, { classification: key }); setClasses([...classes, key]); }
      onSaved?.({ silent: true });
    } catch (e) { toast.error(apiError(e)); }
  };
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/persons/${person.id}`, {
        email: form.email || null, mobile: form.mobile, status: form.status,
        organization_id: form.organization_id || null, custom_fields: { agent_number: form.agent_number || null },
      });
      toast.success("Saved"); onSaved?.();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-md" data-testid="quick-edit-dialog">
        <DialogHeader><DialogTitle>Quick Edit — {person.first_name} {person.last_name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Fld label="Email"><Input value={form.email} onChange={set("email")} data-testid="qe-email" /></Fld>
          <Fld label="Mobile / Phone"><Input value={form.mobile} onChange={set("mobile")} data-testid="qe-phone" /></Fld>
          <div className="grid grid-cols-2 gap-3">
            <Fld label="Status">
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="qe-status"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{STATUS.map((s) => <SelectItem key={s} value={s}>{cap(s)}</SelectItem>)}</SelectContent>
              </Select>
            </Fld>
            <Fld label="Agent Code"><Input value={form.agent_number} onChange={set("agent_number")} data-testid="qe-agent-code" /></Fld>
          </div>
          <Fld label="Organization">
            <Select value={form.organization_id || "none"} onValueChange={(v) => setForm({ ...form, organization_id: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="qe-org"><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent className="bg-white"><SelectItem value="none">Unassigned</SelectItem>
                {orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
            </Select>
          </Fld>
          <Fld label="Classifications">
            <div className="flex flex-wrap gap-1.5">
              {classTypes.map((c) => (
                <button key={c.key} type="button" data-testid={`qe-class-${c.key}`} onClick={() => toggleClass(c.key)} disabled={c.assignable === false}
                  title={c.assignable === false ? "Changed through its own workflow or by an administrator" : undefined}
                  className={`text-xs rounded-full border px-2.5 py-1 disabled:opacity-50 disabled:cursor-not-allowed ${classes.includes(c.key) ? "border-brand bg-brand/10 text-brand" : "border-slate-200 text-slate-500"}`}>{c.label}</button>
              ))}
            </div>
          </Fld>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="qe-save" className="bg-[#006090] hover:bg-[#004E75]">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function MoveOrgDialog({ personIds, sampleName, orgs, onClose, onDone }) {
  const [orgId, setOrgId] = useState("");
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const count = personIds.length;
  const destName = orgs.find((o) => o.id === orgId)?.name;
  const submit = async () => {
    if (!orgId) return;
    setSaving(true);
    try {
      const r = await api.post("/persons/assign-organization", { person_ids: personIds, organization_id: orgId });
      setResult(r.data);
      toast.success(`Moved ${r.data.moved} ${r.data.moved === 1 ? "person" : "people"}`);
      onDone?.();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-md" data-testid="move-org-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ArrowRightLeft className="w-5 h-5 text-[#006090]" /> Move Organization</DialogTitle>
          <DialogDescription>
            Move {count} {count === 1 ? "Person" : "People"}{sampleName ? ` (${sampleName}${count > 1 ? ` +${count - 1} more` : ""})` : ""} to a
            different Organization within this Client Account. Only the Organization changes — Person IDs and all history are preserved.
          </DialogDescription>
        </DialogHeader>
        {!result ? (
          <div className="space-y-3">
            <Fld label="Destination Organization">
              <Select value={orgId} onValueChange={setOrgId}>
                <SelectTrigger data-testid="move-org-select"><SelectValue placeholder="Select organization" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}{o.people_count != null ? ` (${o.people_count})` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </Fld>
            {orgId && (
              <div className="rounded-md bg-amber-50 border border-amber-200 p-2.5 text-[11px] text-amber-700 flex gap-1.5">
                <ShieldAlert className="w-4 h-4 shrink-0" /> Moving to <b>{destName}</b> may change which District users can view these records.
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-2 text-sm" data-testid="move-org-result">
            <p><b>{result.moved}</b> moved to <b>{result.organization_name}</b>.</p>
            {result.already_in_destination > 0 && <p className="text-slate-500">{result.already_in_destination} already in destination (skipped).</p>}
            {result.blocked?.length > 0 && (
              <div className="rounded-md bg-rose-50 border border-rose-200 p-2.5">
                <p className="text-rose-700 font-medium text-xs mb-1">{result.blocked.length} could not be moved:</p>
                <ul className="text-[11px] text-rose-600 list-disc pl-4">
                  {result.blocked.map((b, i) => <li key={i}>{b.name || b.id}: {b.reason}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          {!result ? (
            <>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={submit} disabled={!orgId || saving} data-testid="move-org-confirm" className="bg-[#006090] hover:bg-[#004E75]">Move {count} {count === 1 ? "Person" : "People"}</Button>
            </>
          ) : (
            <Button onClick={onClose} data-testid="move-org-close" className="bg-[#006090] hover:bg-[#004E75]">Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function EditClassificationsDialog({ person, classTypes, onClose, onSaved }) {
  const [classes, setClasses] = useState(person.classifications || []);
  const toggle = async (key) => {
    try {
      if (classes.includes(key)) { await api.delete(`/persons/${person.id}/classifications/${key}`); setClasses(classes.filter((c) => c !== key)); }
      else { await api.post(`/persons/${person.id}/classifications`, { classification: key }); setClasses([...classes, key]); }
      onSaved?.();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-md" data-testid="edit-class-dialog">
        <DialogHeader>
          <DialogTitle>Edit Classifications — {person.first_name} {person.last_name}</DialogTitle>
          <DialogDescription>Changes save immediately and keep People list counts in sync.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-2 py-2">
          {classTypes.map((c) => (
            <button key={c.key} type="button" data-testid={`ec-class-${c.key}`} onClick={() => toggle(c.key)} disabled={c.assignable === false}
              title={c.assignable === false ? "Changed through its own workflow or by an administrator" : undefined}
              className={`text-sm rounded-full border px-3 py-1.5 disabled:opacity-50 disabled:cursor-not-allowed ${classes.includes(c.key) ? "border-brand bg-brand/10 text-brand" : "border-slate-200 text-slate-500"}`}>{c.label}</button>
          ))}
        </div>
        <DialogFooter><Button onClick={onClose} data-testid="ec-done" className="bg-[#006090] hover:bg-[#004E75]">Done</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
